import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Loader2, RefreshCw } from 'lucide-react';
import { useAuth } from '@edusense/shared-auth-core';
import BenefitCampaignSettings from '../benefitCampaign/BenefitCampaignSettings';
import { buildBenefitCampaignRows, formatMetricNumber, toDateInputValue } from '../benefitCampaign/benefitCampaign';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import type { MetricsSummary } from '../types/metrics';

type MetricsSummaryResponse = MetricsSummary;

const inputClasses =
  'flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2';

export default function BenefitCampaignPage() {
  const { token: accessToken } = useAuth();
  const [startDate, setStartDate] = useState(toDateInputValue(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)));
  const [endDate, setEndDate] = useState(toDateInputValue(new Date()));
  const [summary, setSummary] = useState<MetricsSummaryResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const getAuthHeaders = useCallback(() => {
    const headers: Record<string, string> = accessToken ? { Authorization: `Bearer ${accessToken}` } : {};
    const xsrfCookie = document.cookie.split('; ').find((row) => row.startsWith('XSRF-TOKEN='));
    if (xsrfCookie) {
      headers['X-XSRF-TOKEN'] = xsrfCookie.split('=')[1];
    }
    return headers;
  }, [accessToken]);

  const queryParams = useMemo(
    () => ({
      startDate: `${startDate}T00:00:00`,
      endDate: `${endDate}T23:59:59`,
    }),
    [endDate, startDate],
  );

  const loadFunnel = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get<MetricsSummaryResponse>('/api/v1/product-metrics/summary', {
        params: queryParams,
        headers: getAuthHeaders(),
        withCredentials: true,
      });
      setSummary(response.data ?? null);
    } catch (fetchError) {
      console.error('Failed to fetch benefit campaign funnel:', fetchError);
      setSummary(null);
      setError('혜택 캠페인 퍼널 조회에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, queryParams]);

  useEffect(() => {
    loadFunnel();
  }, [loadFunnel]);

  const benefitCampaignRows = buildBenefitCampaignRows(summary);

  return (
    <div className="space-y-8">
      <div className="flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
        <div>
          <h1 className="text-3xl font-bold">혜택 캠페인</h1>
          <p className="text-muted-foreground">첫 로그인 무료 혜택 기간과 지급 조건, 캠페인 퍼널을 관리합니다.</p>
        </div>
        <Button variant="outline" onClick={loadFunnel} disabled={loading}>
          {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
          새로고침
        </Button>
      </div>

      <BenefitCampaignSettings getAuthHeaders={getAuthHeaders} />

      <Card>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <div className="space-y-1.5">
              <label className="text-sm font-medium">퍼널 시작일</label>
              <input className={inputClasses} type="date" value={startDate} onChange={(event) => setStartDate(event.target.value)} />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium">퍼널 종료일</label>
              <input className={inputClasses} type="date" value={endDate} onChange={(event) => setEndDate(event.target.value)} />
            </div>
            <div className="flex items-end">
              <Button className="w-full" variant="outline" onClick={loadFunnel} disabled={loading}>
                {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
                퍼널 조회
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {error ? (
        <Card className="border-destructive">
          <CardContent className="py-4 text-sm text-destructive">{error}</CardContent>
        </Card>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>혜택 캠페인 퍼널</CardTitle>
          <p className="text-sm text-muted-foreground">
            혜택 노출부터 실제 서버 지급 결과까지 캠페인 이벤트 기준으로 집계합니다.
          </p>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-6">
            {benefitCampaignRows.map((row) => (
              <div key={row.code} className="rounded-lg border p-3">
                <p className="text-sm font-medium">{row.label}</p>
                <p className="mt-2 text-2xl font-semibold">{formatMetricNumber(row.count)}</p>
                <p className="mt-1 text-xs text-muted-foreground">이벤트 {formatMetricNumber(row.total)}회</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
